import React, {useRef, useEffect} from 'react';
import styles from "../../../styles/portfolio/projects/distributed_coutner.scss";
import {Link} from "./Link";
import {Quote} from "./Quote";
import {HighlightedText} from "./HighlightedText";
import {Note} from "./Note";
import {useLocation} from "react-router-dom";
import {UnderConstruction} from "./UnderConstruction";
import gif from '../../../resources/images/oauth/demo.gif';
import {androidstudio as syntaxStyle} from "react-syntax-highlighter/dist/cjs/styles/hljs";
import {
    authorizationEtcHosts,
    authorizationLocalSetup,
    authorizationLocalSetupAuthPopup,
    authorizationLocalSetupAuthServer, authorizationLocalSetupAuthServerUserCreation, authorizationLocalSetupBackend,
    authorizationLocalSetupDbAndProxy, authorizationLocalSetupFrontend,
    authorizationLocalSetupIdentityProvider
} from "./code_snippets/CodeSnippets";
import SyntaxHighlighter from "react-syntax-highlighter";
import {LazyLoadImage} from "react-lazy-load-image-component";



export const AuthenticationAsAService = props => {
    const introductionRef = useRef(null);
    const oauthRef = useRef(null);
    const requirementsRef = useRef(null);
    const systemDesignRef = useRef(null);
    const implementationRef = useRef(null);
    const demoRef = useRef(null);
    const deploymentRef = useRef(null);
    const appendixRef = useRef(null);
    const oauthFlowsRef = useRef(null);
    const introspectionRef = useRef(null);
    const dependencyInjectionRef = useRef(null);
    const lambdaContainerizationRef = useRef(null);

    const openIdRef = useRef(null);

    const scrollToSection = (elementRef) => {
        elementRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }

    let {hash} = useLocation();
    let section = hash.replaceAll('#', '');
    useEffect(() => {
        switch (section) {
            case 'introduction':
                scrollToSection(introductionRef);
                break;
            case 'system-design':
                scrollToSection(systemDesignRef);
                break;
            case 'requirements':
                scrollToSection(requirementsRef);
                break;
            case 'implementation':
                scrollToSection(implementationRef);
                break;
            case 'oauth-flows':
                scrollToSection(oauthFlowsRef);
                break;
            case 'introspection':
                scrollToSection(introspectionRef);
                break;
            default:
                break;
        }
    }, [section]);

    return (
        <div className={styles.container}>
            <span>
                <section id="introduction" ref={introductionRef}>
                    <h1>OAuth2 for Single Page Applications</h1>
                    A group of microservices that can be used to implement
                    the <b><Link text={'OAuth2.0'} url={'https://datatracker.ietf.org/doc/html/rfc6749'}/></b> protocol, and provide a UI with a secure way
                    to obtain, use, and refresh an <Link text={'access token'} url={'https://www.oauth.com/oauth2-servers/access-tokens/'}/>.
                    Access tokens can be used to authorize the UI (or any other service) with access to specific parts of a user’s data.
                    The exact same set of microservices can be easily configured to provide access token to any kind of service,
                    but additional effort is required when dealing with a frontend, that must not store sensitive information (neither in cookies, nor in
                    browser's local storage). <br/>
                    {/*The following technologies are used:*/}
                    {/*<ul>*/}
                    {/*    <li>*/}
                    {/*        <b><Link text={'Faust'} url={'https://faust.readthedocs.io/'}/></b>: a library that enables us to do stream processing using pure python.*/}
                    {/*    </li>*/}
                    {/*</ul>*/}

                    <h2>Table of Contents</h2>
                    <li><Link text={'Problem Statement & Requirements'} section={requirementsRef}/></li>
                    <li><Link text={'System Design'} section={systemDesignRef}/></li>
                    <li><Link text={'Implementation'} section={implementationRef}/></li>
                    <li><Link text={'Demo'} section={demoRef}/></li>
                    <li><Link text={'Deployment on AWS'} section={deploymentRef}/></li>
                    <li>
                        <Link text={'Appendix'} section={appendixRef}/>
                        <ul>
                            <li><Link text={'The OAuth 2.0 Authorization Framework'} section={oauthRef}/></li>
                            <li><Link text={'Dependency Injection in python'} section={dependencyInjectionRef}/></li>
                            <li><Link text={'Containerization of lambda functions for local development'} section={lambdaContainerizationRef}/></li>
                        </ul>
                    </li>
                </section>
                <section id="requirements" ref={requirementsRef}>
                    <h2>Problem Statement & Requirements</h2>
                    We need to design an authentication & authorization mechanism that provides a frontend with a secure way
                    to request resources and perform actions from a "stack". The stack could be any number of microservices,
                    but for simplicity we may assume that all services are exposed through
                    an <Link text={'API gateway'} url={'https://www.redhat.com/en/topics/api/what-does-an-api-gateway-do'}/>. <br/>
                    End users should be able to:
                    <ul>
                        <li>Use a third party Authorization provider (e.g. google) to login.</li>
                        <li>Be redirected to our frontend UI after logging to the third party.</li>
                        <li>Stay logged in after refreshing the UI.</li>
                        <li>Logout.</li>
                    </ul>
                    The system should:
                    <ul>
                        <li>
                            Implement
                            the <Link text={'Authorization Code Flow'} url={'https://www.oauth.com/oauth2-servers/access-tokens/authorization-code-request/'}/> as
                            defined by the Oauth2.0.
                        </li>
                        <li>Be extendable in supporting more than one authorization providers.</li>
                        <li>Provide endpoints for secure token exchange.</li>
                        <li>Avoid storing tokens.</li>
                        <li>Ensure that tokens cannot be stolen during exchange or after they have been deliverd to the UI.</li>
                    </ul>
                    The UI where the user will eventually login should:
                    <ul>
                        <li>Avoid storing any tokens, neither in browser's local storage nor in cookies.</li>
                    </ul>

                    <h3>Non-functional Requirements</h3>
                    <ul>
                        <li><b>Scalability</b> <br/>
                            The system should use JTWs in order to avoid storing tokens and allow for easy scaling just by
                            the usage of a load balancer.
                        </li>
                        <li><b>Security</b> <br/>
                            The system should be safe against CSRF, XSS, or Man in the Middle attacks. Also it should ensure
                            that tokens cannot be stolen, and ensure small TTL for access tokens.
                        </li>
                        <li><b>Maintainability</b> <br/>
                            The system should be easily extended to use extra third-party authorization providers.
                        </li>
                    </ul>

                    <h3>Using the token(s) for authentication/Authorization</h3>
                    Since the above requirements may seem to vague for someone unfamiliar with the OAuth2 protocol, it is
                    worth mentioning some more information. The above requirements describe a system that securely exchanges
                    access token (and other tokens) between a third party auth-server and a frontend. After the exchange,
                    frontend may use such tokens to perform actions against an API Gateway (or other microservices).
                    <img className={styles.image} src={require('../../../resources/images/oauth/overview.png')} /> <br/>
                    The above figure shows a classic scenario of token usage. Let's explain the steps 0-4:
                    <ul>
                        <li>Step 0: The token is securely provided to frontend - this is what we will design in later sections.</li>
                        <li>
                            Step 1: The frontend makes any request to the API Gateway. For all authenticated requests the access token
                            must be included in the request (usually in the Authorization header).
                        </li>
                        <li>
                            Step 2: The API Gateway validates the access token, and ensures that it has permissions to perform
                            the requested action. Validation may differ based on the access token type. In case
                            of <Link text={'JTWs'} url={'https://jwt.io/'}/> API Gateway will validate the jwt's signature and if
                            valid it will trust any claim included in the token (stateless). In case of bearer tokens API Gateway
                            may validate the token using
                            the <Link text={'introspection endpoint'} url={'https://www.oauth.com/oauth2-servers/token-introspection-endpoint/'}/> of
                            the third-party authorization server.
                        </li>
                        <li>
                            Step 3: Optionally, API Gateway may need to get the user-id from the access token. Again, if access token is a JWT the
                            user id is usually included in the <HighlightedText text={'sub-claim'}/>, but if we have a bearer token the
                            introspection endpoint must be used. This user id may be used to correlate access-token with a user-entity but it
                            won't be used for authorization. If we want to apply permission logic, the <HighlightedText text={'scopes'}/> of
                            the access token must be used.
                        </li>
                        <li>
                            Step 4: This step is completely optional, and it differs in distinct teams/organizations. If role-based access
                            is applied per microservice, then the access token may be added to all additional requests, and each microservice
                            should be able to validate the token and ensure that it has enough permissions for the requested action.
                            <Note
                                header={'Note'}
                                message={'A common approach is that API Gateway constructs its own access_token, including user\'s claims.' +
                                'This way the rest of microservices don\'t need to handle tokens provided by different third-party authorization servers' +
                                ', since they only need to validate the token created and signed by API Gateway.'}
                            />
                        </li>
                    </ul>

                </section>

                <section id="system-design" ref={systemDesignRef}>
                    <h2>System Design</h2>
                    If the reader is not comfortable with the Oauth terminology, it might be useful to checkout
                    the <Link text={'OAuth 2.0 Authorization Framework'} section={oauthRef}/> before reading this section. <br/>
                    Let's start with a high level overview of the components that we will use:
                    <ul>
                        <li>
                            A <b>Frontend</b> and a <b>Backend</b> that will act as the Oauth Client.
                            The Frontend will be designed as an <Link text={'SPA'} url={'https://en.wikipedia.org/wiki/Single-page_application'}/> served
                            by the backend. In addition, the Backend will handle the token retrieval after redirection from the
                            Authorization Server, and the secure token exchange with the frontend, using http-only cookies.
                        </li>
                        <li>
                            An <b>Identity-Provider</b> microservice, whose responsibility is to manage user entities to our system.
                            Users will not be able to use our UI unless they have previously signup. In both the login and the signup flows
                            the end-users will use the third-party authorization server to obtain a token, but then the Backend will use
                            the <HighlightedText text={'identity-provider'}/> to ensure that the users exists in our system, or create a new user,
                            depending on the flow.
                        </li>
                        <li>
                            An <b>Authorization Server</b> that will be used for test purposes.
                            On real production environments a third-party authorization
                            server should be used (current implementation supports google's auth-server).
                        </li>
                    </ul>
                    The above components are supporting three flows:
                    <ul>
                        <li><b>Sign up</b>, where user is authenticated using the external Authorization Server and then a new user entry is
                        created to our system.</li>
                        <li><b>Login</b>, where user is authenticated using the external Authorization Server and if a user entry for it exists in
                        our system, the users logs in.</li>
                        <li><b>Serverless Login</b>, where user is authenticated using the external Authorization Server and no user entry is created in our system.</li>
                        <li><b>Logout</b>, where user is logout out of our system.</li>
                    </ul>

                    <h3>Serverless Login flow</h3>
                    We will start with the <HighlightedText text={'Serverless Login flow'}/> since it is more simple, and the following flows are just extensions on top
                    of this flow. The figure bellow describes the Authorization Code Grant flow for Single Page Applications as we have
                    already described in <Link text={'OAuth Flows section'} section={oauthFlowsRef}/>:
                    <img className={styles.image} src={require('../../../resources/images/oauth/serverless_login.png')} /> <br/>
                    <ul>
                        <li>
                            <b>Step 1</b>: User visits the website. In order to be protected from CSRF attacks, the website
                            must include (in a http-only cookie)
                            a <HighlightedText text={'CSRF token'}/>. If the cookie does not exist, the Frontend issues a redirect
                            to a specific Backend url, which in turn redirects to the website and adds the corresponding cookie.
                        </li>
                        <li>
                            <b>Step 2</b>: User clicks on login and the Frontend redirects him to the Authorization Server
                            of the corresponding third-party (e.g. Google, Linkedin, etc ...).
                        </li>
                        <li>
                            <b>Step 3 & 4</b>: The third party Authorization Server handles user login and responds with a redirect (302),
                            which includes the <HighlightedText text={'authorization code'}/>. The popup window is closed.
                        </li>
                        <li>
                            <b>Step 5</b>: After the authorization popup is close, a redirect is made to a
                            predefined <HighlightedText text={'redirect_uri'}/>, including the authorization code.
                            This redirect is made to an endpoint of the Backend, which is configured to read the Cookie
                            with the <HighlightedText text={'CSRF token'}/>.
                        </li>
                        <li>
                            <b>Step 6</b>: Backend validates the CSRF token, and then makes
                            a <HighlightedText text={'POST /token'}/> request to the Authorization Server, with the authorization code,
                            in order to get the <HighlightedText text={'access'}/>, <HighlightedText text={'refresh'}/>,
                            and <HighlightedText text={'id'}/> tokens. This request is part of
                            the <Link text={'back channel'} url={'https://stackoverflow.com/questions/25161803/what-does-the-term-backchannel-request-means-and-how-to-make-backchannel-reque'}/> thus
                            tokens are exchanged in a secure way.
                            <Note
                                header={'Extensibility Note'}
                                message={'As the last action of Step 6, the Backend can create its own tokens, based on ' +
                                'the third-party tokens. This increases the systems extensibility, since the rest of the services' +
                                ' are not required to include logic for the third-party tokens. Also a single token type enables ' +
                                'serverless scaling, in case the the constructed tokens are JWTs.'}
                            />
                        </li>
                        <li>
                            <b>Step 7</b>: In order to securely exchange tokens with the Frontend, the Backend responds with a redirect to the Frontend
                            including the tokens in Http-Only Cookies.
                        </li>
                        <li>
                            <b>Step 8 & 9</b>: The Frontend recognises that tokens are included in Http-Only Cookies (a non http-only cookie is used) and
                            makes a request to a specific Backend endpoint. This endpoint is designed to retrieve the tokens from the http-only cookies,
                            remove the access token from the cookie, and return it in the response body.
                        </li>
                        <Note
                            header={'Security Note'}
                            message={<div>
                                Steps 7, 8, and 9 are crucial to ensure the secure token exchange.
                                <ul>
                                    <li>
                                        If Frontend was storing the tokens it would be vulnerable to XSS attacks.
                                    </li>
                                    <li>
                                        If Frontend was keeping all tokens in cookies it would be vulnerable to Injection attacks.
                                    </li>
                                    <li>
                                        If Frontend was keeping all tokens in memory, then it would force user to re-login
                                        every time that he/she updated the page.
                                    </li>
                                </ul>
                            </div>}
                            type={'warn'}
                        />
                    </ul>

                    <h3>Sign-Up flow</h3>
                    The sign up flow just extends on the <HighlightedText text={'Serverless Login flow'}/>. In this case we introduce a new service,
                    the <HighlightedText text={'Identity Provider'}/> which is responsible for:
                    <ul>
                        <li>
                            User creation and lifecycle management in our system.
                            <Note
                                header={'Note'}
                                message={'Identity Provider only manages the user entities but does not handle any password storage, or' +
                                ' authentication aspects.'}
                            />
                        </li>
                        <li>Role creation, match with user entities, and lifecycle management.</li>
                    </ul>
                    The new flow allows users to authenticate through an external Authorization Provider, create account in our system, and then
                    the token is returned to the Frontend (thus they are considered logged in):
                    <img className={styles.image} src={require('../../../resources/images/oauth/sign_up.png')} /> <br/>
                    The new steps in this flow are:
                    <ul>
                        <li>
                            <b>Step 6.1</b>: Upon token retrieval from the Authorization Server, the Backend decodes the access token
                            and extracts the <HighlightedText text={'user external identifier'}/>. If the access token is a Bearer token,
                            then the <Link text={'introspection endpoint'} section={introspectionRef}/> is used to retrieve user's external identifier.
                            Then, the Backend makes a <HighlightedText text={'POST /user'}/> request, in order to create a user with such an identifier.
                        </li>
                        <li>
                            <b>Step 6.2</b>: The <HighlightedText text={'Identity Provider'}/> creates the user and responds with user's data
                            (that may include an internal identifier), which will be used by Backend in order to create a new access token.
                            The new access token, among with refresh and id token are returned to the Frontend, following the steps that we
                            have already described.
                        </li>
                    </ul>

                    <h3>Login flow</h3>
                    The login flow is almost identical to the <HighlightedText text={'Sign-Up flow'}/>:
                    <img className={styles.image} src={require('../../../resources/images/oauth/login_flow.png')} /> <br/>
                    The only difference is that on <b>Step 6.1</b>, instead of creating the user we are requesting the user from
                    the <HighlightedText text={'Identity Provider'}/>. If the user if found the above flow is used, but if
                    user is not found a 401 is returned.

                    <h3>Logout flow</h3>
                    The Backend exposes a specific <HighlightedText text={'/logout'}/> endpoint that the Frontend uses.
                    This endpoint redirects to the Frontend but it prior deletes any token-related cookies.
                    Depending on the implementation, this endpoint may also contact Authorization Server to expire the tokens,
                    but this is completely optional in our design.

                    <h3>Security Considerations</h3>
                    We have already discussed how we can securely exchange tokens using the Authorization Provider, Backend and Frontend
                    components. However we have not mentioned how we can be protected
                    against <Link text={'Session Hijacking'} url={'https://owasp.org/www-community/attacks/Session_hijacking_attack'}/> attacks.
                    In our design, such an attack is only possible in two ways:
                    <ul>
                        <li>Browser is hacked, and the in-memory variables of the frontend are exposed, thus the access token can be stolen.</li>
                        <li>Server is hacked, meaning that either the backend component, Load Balancer or network expose the access token somehow
                        (could also be that the access token is shown in some logs).</li>
                    </ul>
                    In both cases, an attacker who obtains the token can act as the Frontend, making requests to the Backend pretending to be
                    the actual user. In order to protect against such an attack, all we have to do is:
                    <ul>
                        <li>Always create our own access token (let's assume it is a JWT).</li>
                        <li>Attach a <HighlightedText text={'session_id'}/> in the access_token.</li>
                        <li>Return an Http-Only Cookie containing the <HighlightedText text={'session_id'}/>.</li>
                        <li>Backend always compares the session id found in the cookie with the one included in the access token.</li>
                    </ul>
                    This way Backend is always able to ensure that the request is made by the same browser that made the initial request,
                    and received the access token, and not by someone who has steal it.

                </section>
                <section id="implementation" ref={implementationRef}>
                    <h2>Implementation</h2>
                    In order to implement the components discussed in the above sections, the following projects have been created:
                    <ul>
                        <li>
                            <b><Link text={'Identity provider'} url={'https://github.com/chrisbek/identity-provider'}/></b>: implements the above mentioned identity provider.
                        </li>
                        <li>
                            <b><Link text={'Identity-provider-client'} url={'https://github.com/chrisbek/identity-provider-client'}/></b>: a python client that can be used to consume
                            the identity-provider service. Client is auto-generated (using OpenAPI Generator).
                        </li>
                        <li>
                            <b><Link text={'Frontend'} url={'https://github.com/chrisbek/oauth-frontend'}/></b>: an implementation of the above Frontend, that only supports the login, sign-up, and logout flows.
                        </li>
                        <li>
                            <b><Link text={'Backend'} url={'https://github.com/chrisbek/oauth-backend'}/></b>: an implementation of the Backend as it has been described in the above sections.
                        </li>
                        <li>
                            <b><Link text={'Authorization Server'} url={'https://github.com/chrisbek/authorization-service'}/></b>: a simple, insecure authorization server, built for easing the local development.
                            This implementation uses the <HighlightedText text={'Identity Provider'}/> as the user pool.
                        </li>
                        <li>
                            <b><Link text={'Authorization Popup UI'} url={'https://github.com/chrisbek/authorization-server-popup'}/></b>: a frontend used by the Local Authorization Server in order to sign up the user.
                        </li>
                        <li>
                            <b><Link text={'Local Stack'} url={'https://github.com/chrisbek/local-stack'}/></b>: a project including the necessary docker compose files for creating and starting the databases
                            (both MariaDB and DynamoDB), and the reverse proxy, necessary for local development.
                        </li>
                        <li>
                            <b>Basic Infrastructure</b>: a terraform project that creates the very basic infrastructure required
                            in order to deploy any of the above services to AWS.
                        </li>
                    </ul>
                    In addition, the current frontend (that is, this website) has a specific page that supports the serverless login flow, as
                    you can test on the <Link text={'demo section'} section={demoRef}/>.

                </section>
                <section id="demo" ref={demoRef}>
                    <h2>Demo</h2>
                    If you want to test the <HighlightedText text={'Serverless Login flow'}/> you can do it by visiting the DEMO PAGE.
                    If you want to view how to setup and test the rest of the flows locally, read the following section.

                    <h3>Local Setup</h3>
                    The high-level architecture of the local setup is given bellow:
                    <img className={styles.image} src={require('../../../resources/images/oauth/auth_local_architecture.png')} /> <br/>
                    Nginx is used as a reverse proxy that exposes two domain names
                    (<HighlightedText text={'authentication.local'}/>, and <HighlightedText text={'authorization-server.local'}/>).
                    Based on the prefix, it can either redirect to an express server, that serves frontends' static files, or redirect the traffic
                    to the corresponding api (Backend, or Local Authorization Server). Backends make use of relational and non-relational
                    (DynamoDB) databases. <br/>
                    Bellow are the exact steps on how to configure this projects:
                    <ul>
                        <li>
                            <b>Clone the projects</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetup}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Add the following to your <HighlightedText text={'/etc/hosts'}/></b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationEtcHosts}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Start local databases & reverse-proxy</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupDbAndProxy}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Setup Identity Provider</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupIdentityProvider}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Setup Authorization Popup window</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupAuthPopup}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Setup Local Authorization Server</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupAuthServer}
                            </SyntaxHighlighter>
                            Also we need to create one (or more) user(s) in the Authorization Server, so that we can login
                            using their credentials. You can do so by running:
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupAuthServerUserCreation}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Setup Frontend</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupFrontend}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Setup Backend</b> <br/>
                            <SyntaxHighlighter language="bash" style={syntaxStyle} className={styles.codeContainer}>
                                {authorizationLocalSetupBackend}
                            </SyntaxHighlighter>
                        </li>
                        <li>
                            <b>Visit <HighlightedText text={'authentication.local'}/> on your browser.</b>
                        </li>
                    </ul>

                    Finally, a demo of the local flows is given in the gif bellow: <br/>
                    {/*<img className={styles.gif} src={gif} alt="loading..." />*/}
                    <LazyLoadImage
                        alt={"demo-image"}
                        src={gif}
                        className={styles.gif}
                        placeholderSrc={require('../../../resources/images/loading.gif')}
                    />

                </section>
                <section id="deployment" ref={deploymentRef}>
                    <h2>Deployment on AWS</h2>
                    <UnderConstruction />
                </section>
                <section id="appendix" ref={appendixRef}>
                    <h2>Appendix</h2>
                    <section id="oauth2" ref={oauthRef}>
                        <h3>The OAuth 2.0 Authorization Framework</h3>
                        In a bare-bones client-server authentication architecture, the client authenticates with a server to
                        access protected resources using the user’s credentials.
                        In order to provide third-party applications access to such protected resources,
                        the user has to share their credentials with the third party as well, but this approach creates
                        several problems while it is prone to various attacks.
                        OAuth2 was drafted to combat this scenario with a much more structured architecture that would
                        instead be more secure and act as a  stepping-stone for the protocols that followed. <br/>
                        As mentioned in <Link text={'[RFC 6749]'} url={'https://datatracker.ietf.org/doc/html/rfc6749'}/>:
                        <Quote
                            message={'The OAuth 2.0 authorization framework enables a third-party application to obtain' +
                            ' limited access to an HTTP service, either on behalf of a resource owner by orchestrating' +
                            ' an approval interaction between the resource owner and the HTTP service,' +
                            ' or by allowing the third-party application to obtain access on its own behalf.' +
                            ' This specification replaces and obsoletes the OAuth 1.0 protocol described in [RFC 5849].'}
                        />

                        <h4>Authentication vs Authorization</h4>
                        Since these concepts often use confusion it is worth mentioning that:
                        <ul>
                            <li><b>Authorization</b> determines what resources a user can access.</li>
                            <li><b>Authentication</b> verifies who the user is.</li>
                        </ul>
                        OAuth 2.0 does not provide a mechanism to say who a user is or how they authenticated,
                        it just says that a user delegated an application to act on their behalf.
                        That means that OAuth 2.0 deals only with Authorization. In addition, the protocol does not specify
                        the format of the tokens. <br/>
                        According to the specifications an Access Token should always be used for authorization,
                        while an ID Token can only be used for authentication. In practice it is fairly common to:
                        <ul>
                            <li>
                                Include a <HighlightedText text={'user-identifier'}/> in the access_token. This identifier
                                is only known by the Authorization Server. The Resource Server cannot obtain the user identity using this identifier.
                                While it is possible to relate the external user-id with a user entity of the Resource Server,
                                applying role-based access based on this identifier is a violation of the protocol.
                            </li>
                            <li>
                                Include oauth scopes in the access_token, which are used by the backend services in order to perform
                                role-based access.
                            </li>
                            <li>
                                Include user related information in the <HighlightedText text={'id_token'}/> (see <Link text={'OpenID'} section={openIdRef}/>).
                                These information are displayed by the frontend, but they are not used by the "backend" services.
                            </li>
                        </ul>

                        <h4>Oauth Terminology</h4>

                        <h5>Resource Owner</h5>
                        The server hosting the protected resources, capable of accepting and responding to protected
                        resource requests using access tokens. In our case ResourceOwner is the API Gateway.

                        <h5>Resource Server</h5>
                        The server hosting the protected resources, capable of accepting and responding to
                        protected resource requests using access tokens. In our case resource server is the API Gateway.

                        <h5>Client</h5>
                        An application making protected resource requests on behalf of the resource owner and with its authorization.
                        The term "client" does not imply any particular implementation characteristics
                        (e.g. whether the application executes on a server, a desktop, or other devices). <br/>
                        In our case we have pair of frontend-backend solution: the frontend is an SPA used by our users.
                        The backend is responsible for serving this SPA to the browser of the end users.
                        The frontend may keep the client_id while the backend can have the secret_id.
                        Any request for tokens requires both client_id and secret_id, thus it should be made through the backend.

                        <h5>Authorization Server</h5>
                        The server issues access tokens to the client after successfully authenticating the resource owner and obtaining authorization.
                        In our case a third-party authorization server will be used, in order to avoid dealing with user's passwords storage,
                        scalability issues, etc.

                        <section id="oauth-flows" ref={oauthFlowsRef}>
                            <h4>Oauth Flows</h4>
                            One read <Link text={'about the different oauth flows'} url={'https://www.oauth.com/'}/>,
                            but here we will focus on
                            the <Link text={'Authorization Code Grant'} url={'https://www.oauth.com/oauth2-servers/access-tokens/authorization-code-request/'}/>.
                            The following diagram shows the most important components involved in this flow: end users (Resource owner), frontend-backend (Client),
                            and the Authorization Server:
                            <img className={styles.image} src={require('../../../resources/images/oauth/auth_code_grant.png')} /> <br/>
                            Here is the high level overview of the Authorization Code Grant flow:
                            <ul>
                                <li>Client (frontend) provides authorization options to the user.</li>
                                <li>Users clicks to one of these options.</li>
                                <li>Users is redirected to the corresponding Authorization Server.</li>
                                <li>Authorization Server responds by rendering a UI where user inserts its username and password.</li>
                                <li>Users submits its credentials which are then verified by the Authorization Server.</li>
                                <li>
                                    Authorization Server responds with a redirect to the
                                    preconfigured <HighlightedText text={'redirect_uri'}/> which points to a specific GET endpoint on Client (backend).
                                    The redirect includes the <HighlightedText text={'authorization code'}/> in the url. Authorization code has a very small TTL.
                                </li>
                                <li>
                                    Client (backend) uses the authorization code together
                                    with <HighlightedText text={'client_id'}/> and <HighlightedText text={'client_secret'}/> in order
                                    to obtain refresh and access tokens (also id token in case of OpenID).
                                </li>
                                <li>
                                    Client securely exchanges the tokens to the UI. The backend responds with a redirect that contains all tokens
                                    in <HighlightedText text={'http-only cookies'}/>. Then, frontend is making a PUT/POST request to a backend's endpoint,
                                    for which these cookies are configured to be included. Backend is resolving the incoming cookies and responds to frontend's
                                    request with access token (included in the response body), as well as refresh token included in a http-only cookie. Finally,
                                    the frontend keeps access token in memory, but <b>it does not store it anywhere (neither cookies, nor local storage)</b>.
                                    When a page refresh occurs, access token is lost, but the refresh token in the http only cookie can be used to obtain a new one.
                                </li>
                                <Note
                                    header={'HTTP-Only Cookies'}
                                    message={'A cookie with the HttpOnly attribute is inaccessible to the JavaScript, and that\'s what makes it perfect' +
                                    'for secure token exchange between a frontend and a backend. This way backend can sent and receive cookies, without the' +
                                    'js code of frontend ever being able to access them.'}
                                />
                            </ul>

                            <Note
                                header={'Warning'}
                                message={<div>
                                    The above flow is vulnerable to <Link text={'CSRF attacks'} url={'https://owasp.org/www-community/attacks/csrf'}/>. For
                                    this reason an the <HighlightedText text={'CSRF token / state'}/> value is used in Single Page Apps,
                                    or <Link text={'PKCE'} url={'https://oauth.net/2/pkce/'}/> in mobile apps.
                                </div>}
                                type={'warn'}
                            />
                        </section>

                        <section id="introspection" ref={introspectionRef}>
                            <h4>Introspection endpoint</h4>
                            The <Link text={'OAuth 2.0 Token Introspection extension'} url={'https://www.oauth.com/oauth2-servers/token-introspection-endpoint/'}/> defines
                            a protocol that returns information about an access token, intended to be used by resource servers or other internal servers. <br/>

                            <h4 ref={openIdRef}>OpenID</h4>
                            As defined by the <Link text={'OIDC Specs'} url={'https://openid.net/specs/openid-connect-core-1_0.html'}/>:
                            <Quote
                                message={'OpenID Connect 1.0 is a simple identity layer on top of the OAuth 2.0 protocol. ' +
                                'It enables Clients to verify the identity of the End-User based on the authentication ' +
                                'performed by an Authorization Server, as well as to obtain basic profile information ' +
                                'about the End-User in an interoperable and REST-like manner.'}
                            />
                            OIDC was created because OAuth2 was mis-used for both Authorization and Authentication.
                            OIDC is actually an Authentication layer on top of OAuth2, that uses id_tokens.

                            <h5>Id Token</h5>
                            The ID Token is a security token that contains Claims about the Authentication of an End-User
                            by an Authorization Server when using a Client, and potentially other requested Claims.
                            The ID Token is represented as a <Link text={'JSON Web Token (JWT)'} url={'https://jwt.io/'}/>.
                        </section>
                    </section>
                </section>

                <section id="dependency-injection-in-py" ref={dependencyInjectionRef}>
                    <h3>Dependency Injection in Python</h3>
                    <UnderConstruction />
                </section>

                <section id="dependency-injection-in-py" ref={lambdaContainerizationRef}>
                    <h3>Containerization of lambda functions for local development</h3>
                    <UnderConstruction />
                </section>
            </span>
        </div>
    )
};
